import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ClusterContactUpdateDto } from 'src/api/v2';
import { LoadingPage } from 'src/components/LoadingPage/LoadingPage';
import { useContact } from 'src/features/contacts';
import useMembers from 'src/hooks/use-members';
import { toastPromise } from 'src/utils/toast';
import ContactInformationForm from './ContactInformationForm';

type Props = {
  contactId?: string;

  onSubmit: () => void;
  onCancel?: () => void;

  showContactDetailLink?: boolean;
};

const ContactInformationFormLogic: React.FC<Props> = ({
  contactId,
  onSubmit,
  onCancel,
  showContactDetailLink,
}) => {
  const { t: tError } = useTranslation('error');

  const { contact, updateMutation, isLoading: isLoadingContact } = useContact(contactId);
  const { combined, isLoading: isLoadingMembers } = useMembers();

  const isLoading = useMemo(() => isLoadingContact || isLoadingMembers, [isLoadingContact, isLoadingMembers]);

  const handleOnSubmit = (data: ClusterContactUpdateDto): void => {
    if (!contact) {
      return;
    }

    const record: ClusterContactUpdateDto = {
      ...data,

      interestAreaIds: contact.interestAreas.map((x) => x.id),
    };

    if (!data.clusterMemberId) {
      record.clusterMemberId = undefined;
    }

    const promise = updateMutation.mutateAsync(record);

    toastPromise(promise, {
      error: tError('contact.update.information'),
    }).then(() => {
      onSubmit?.();
    });
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <ContactInformationForm
      onSubmit={handleOnSubmit}
      onCancel={onCancel}
      contact={contact}
      members={[...combined]}
      showContactDetailLink={showContactDetailLink}
    />
  );
};

export default ContactInformationFormLogic;
